<template>
  <TextHTML
    :custom-class="customClass"
    :html="html"
    :renderComponents="renderAttachments"
  />
</template>

<script lang="ts">
import Vue, { defineComponent, inject, PropType, Ref } from "vue"
import TextHTMLAttachmentListItem from "components/TextHTMLAttachmentListItem.vue"
import vuetify from "initializers/vuetify"
import { Attachment } from "suus-api"
import TextHTML from "components/UI/TextHTML.vue"
import RenderedImage from "components/Wysiwyg/RenderedImage.vue"

export default defineComponent({
  name: "RenderedWysiwygContent",
  components: { TextHTML },
  props: {
    html: String,
    attachments: { type: Array as PropType<Attachment[]>, default: [] },
    loadAttachment: {
      type: Function as PropType<(frontendId: string) => Promise<Attachment>>,
      default: async () => {
        console.log("No loadAttachment function provided in TextHTML.")
      },
    },
    customClass: { type: String, default: "" },
  },
  setup(props) {
    const lightboxService = inject("lightboxService", null)
    if (!lightboxService) {
      console.error(
        "No lightbox service found in TextHTML. Make sure to provide the lightbox service in the app-root."
      )
    }

    const renderAttachments = async (root: Ref<HTMLElement>) => {
      if (root.value) {
        const attachmentNodes = root.value.querySelectorAll("attachment-item")
        for (const nodeKey in attachmentNodes) {
          const node = attachmentNodes[nodeKey]
          if (!node.getAttribute) {
            continue
          }
          const frontendId = node.getAttribute("data-frontend-id")
          let attachment = props.attachments.find(
            (a) => a.frontend_id === frontendId
          )
          attachment ||= await props.loadAttachment(frontendId)

          const imageMode = node.getAttribute("data-image-mode") || "attachment"
          if (!attachment) {
            console.error("Expected attachment not found in TextHTML.")
          } else if (imageMode === "attachment") {
            const ComponentClass = Vue.extend(TextHTMLAttachmentListItem)
            const instance = new ComponentClass({
              propsData: {
                attachment,
                lightboxService,
              },
              vuetify: vuetify,
            })
            instance.$mount()

            if (node.parentNode) {
              node.parentNode.replaceChild(instance.$el, node)
            } else {
              console.error("Parent node not found in TextHTML.")
            }
          } else {
            const imageSize = node.getAttribute("data-image-size")
            const imageSrc = attachment.links.original_url
            const lightboxHeight = attachment.height
            const lightboxWidth = attachment.width
            const ComponentClass = Vue.extend(RenderedImage)
            const instance = new ComponentClass({
              propsData: {
                src: imageSrc,
                size: imageSize || undefined,
                lightboxHeight,
                lightboxWidth,
                displayMode: imageMode || undefined,
                fileName: attachment.name,
                lightboxService,
              },
              vuetify: vuetify,
            })
            instance.$mount()
            if (node.parentNode) {
              node.parentNode.replaceChild(instance.$el, node)
            } else {
              console.error("Parent node not found in TextHTML.")
            }
          }
        }
      } else {
        console.error("Root element not found in TextHTML.")
      }
    }

    return { renderAttachments }
  },
})
</script>
